import html2canvas from 'html2canvas';
import { fetchPublishedAssetAsDataUrl } from '../components/AssetExplorer/AssetExplorer.api';

const THUMBNAIL_WIDTH = 720;
const THUMBNAIL_MAX_HEIGHT = 960;

/**
 * Per-image safety timeout. If a remote asset takes longer than this to
 * resolve (e.g. unreachable CDN), the thumbnail capture still proceeds so the
 * Save action is never blocked indefinitely.
 */
const IMAGE_LOAD_TIMEOUT_MS = 8000;

/**
 * Returns true when a URL is a "remote" cross-origin reference that html2canvas
 * would need a tainted canvas to rasterize. Same-origin URLs and inline data:
 * URLs are excluded — they're already safe.
 */
const isRemoteHttpUrl = (src: string | null | undefined): boolean => {
    if (!src) return false;
    if (src.startsWith('data:') || src.startsWith('blob:')) return false;
    if (!/^https?:\/\//i.test(src)) return false;
    try {
        const u = new URL(src);
        return u.origin !== window.location.origin;
    } catch {
        return false;
    }
};

/**
 * For every `<img>` in `root` whose `src` is a remote (cross-origin) URL,
 * fetch the bytes through the same-origin published-asset proxy and replace
 * the `src` with the resulting `data:` URL.
 *
 * After this runs, html2canvas only sees same-origin / inline images and
 * NEVER taints its canvas — so `toDataURL()` always succeeds, regardless of
 * whether the S3 bucket or CloudFront in front of it has CORS configured.
 *
 * Images that the proxy cannot fetch (non-published, 404, etc.) are left
 * with their original `src` — the capture will still proceed and those
 * images may render as broken if CORS isn't configured for them.
 */
const inlineRemoteImagesAsDataUrls = async (root: HTMLElement): Promise<void> => {
    const imgs = Array.from(root.querySelectorAll('img')) as HTMLImageElement[];
    if (imgs.length === 0) return;

    await Promise.all(
        imgs.map(async (img) => {
            const src = img.getAttribute('src') || img.src;
            if (!isRemoteHttpUrl(src)) return;
            try {
                const dataUrl = await fetchPublishedAssetAsDataUrl(src);
                if (dataUrl) {
                    img.setAttribute('src', dataUrl);
                    // Once src is a data: URL, crossOrigin must be unset — Chrome
                    // treats data URLs as same-origin and an explicit `anonymous`
                    // can interfere with caching of the decoded bitmap.
                    img.removeAttribute('crossorigin');
                }
            } catch {
                /* fall through: keep the original src; capture may show it broken */
            }
        }),
    );
};

/**
 * Inject `crossorigin="anonymous"` into every `<img>` tag of the markup
 * (skipping `data:` URLs and tags that already declare a crossorigin policy).
 *
 * Reason: html2canvas can only rasterize a remote image into a NON-tainted
 * canvas — required by `toDataURL()` — when the browser fetched that image
 * with a CORS request and the origin returned an `Access-Control-Allow-Origin`
 * header. The browser only opts into CORS when `crossorigin` is present on
 * the `<img>` tag at parse time, so we must mutate the markup BEFORE inserting
 * it into the DOM (mutating `.crossOrigin` after the fact is too late and
 * does not retro-actively trigger a CORS request).
 */
const addCrossOriginToImgTags = (html: string): string => {
    if (!html) return html;
    return html.replace(/<img\b([^>]*)>/gi, (match, attrs: string) => {
        if (/\bcrossorigin\s*=/i.test(attrs)) return match;
        const srcMatch = attrs.match(/\bsrc\s*=\s*["']([^"']*)["']/i);
        if (srcMatch && srcMatch[1].startsWith('data:')) return match;
        return `<img${attrs} crossorigin="anonymous">`;
    });
};

/**
 * Waits for every `<img>` inside `root` to either finish loading or
 * definitively fail. Returns once all images have settled (or after
 * `IMAGE_LOAD_TIMEOUT_MS` per image).
 *
 * This is critical for email templates that reference remote CDN URLs (e.g.
 * S3-published assets) — without this wait, html2canvas captures the DOM
 * before those images have downloaded, leaving them blank in the thumbnail.
 *
 * Remote images that don't already declare `crossOrigin` are upgraded to
 * `anonymous` so html2canvas (with `useCORS: true`) can rasterize them when
 * the origin returns the appropriate `Access-Control-Allow-Origin` header.
 */
const waitForAllImagesToLoad = async (root: HTMLElement): Promise<void> => {
    const imgs = Array.from(root.querySelectorAll('img')) as HTMLImageElement[];
    if (imgs.length === 0) return;

    const pending = imgs.map((img) => {
        if (img.src && !img.src.startsWith('data:') && !img.crossOrigin) {
            try {
                img.crossOrigin = 'anonymous';
            } catch {
                /* some browsers ignore late assignment; safe to continue */
            }
        }

        if (img.complete && img.naturalWidth > 0) {
            return Promise.resolve();
        }
        if (img.complete && img.naturalWidth === 0 && img.src) {
            return Promise.resolve();
        }

        return new Promise<void>((resolve) => {
            let settled = false;
            const finish = () => {
                if (settled) return;
                settled = true;
                img.removeEventListener('load', finish);
                img.removeEventListener('error', finish);
                clearTimeout(timer);
                resolve();
            };
            const timer = window.setTimeout(finish, IMAGE_LOAD_TIMEOUT_MS);
            img.addEventListener('load', finish, { once: true });
            img.addEventListener('error', finish, { once: true });
        });
    });

    await Promise.all(pending);
};

/**
 * Captures an element as a thumbnail, cropping tall content to the
 * top portion. Suited for short-form content (SMS, WhatsApp, etc.).
 */
const captureAndCrop = async (container: HTMLElement): Promise<string | null> => {
    document.body.appendChild(container);
    try {
        await inlineRemoteImagesAsDataUrls(container);
        await waitForAllImagesToLoad(container);
        await new Promise((resolve) => setTimeout(resolve, 120));

        const canvas = await html2canvas(container, {
            width: parseInt(container.style.width) || 600,
            backgroundColor: '#ffffff',
            scale: 2,
            logging: false,
            useCORS: true,
            allowTaint: true,
        });

        const aspectRatio = canvas.height / canvas.width;
        const thumbHeight = Math.min(
            Math.round(THUMBNAIL_WIDTH * aspectRatio),
            THUMBNAIL_MAX_HEIGHT,
        );

        const thumbCanvas = document.createElement('canvas');
        thumbCanvas.width = THUMBNAIL_WIDTH;
        thumbCanvas.height = thumbHeight;
        const ctx = thumbCanvas.getContext('2d');
        if (!ctx) return null;

        ctx.drawImage(
            canvas,
            0, 0, canvas.width, Math.min(canvas.height, (THUMBNAIL_MAX_HEIGHT / THUMBNAIL_WIDTH) * canvas.width),
            0, 0, THUMBNAIL_WIDTH, thumbHeight,
        );

        return thumbCanvas.toDataURL('image/png');
    } catch (err) {
        console.error('Thumbnail generation failed:', err);
        return null;
    } finally {
        document.body.removeChild(container);
    }
};

/**
 * Generates a PNG thumbnail from an HTML email template by rendering
 * it offscreen at full dimensions and scaling the ENTIRE content down
 * proportionally so 100% of the email is visible in the thumbnail.
 *
 * The container is rendered wide enough to accommodate the email's
 * inner content table (typically 640px) plus side margins, then
 * html2canvas captures the full scrollable area.
 */
export const generateThumbnailFromHtml = async (
    htmlContent: string,
): Promise<string | null> => {
    if (!htmlContent || htmlContent.trim().length === 0) return null;

    const INITIAL_WIDTH = 800;

    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '0';
    container.style.width = `${INITIAL_WIDTH}px`;
    container.style.backgroundColor = '#ffffff';
    container.innerHTML = addCrossOriginToImgTags(htmlContent);

    document.body.appendChild(container);

    try {
        // STEP 1 — Inline every remote (cross-origin) `<img>` as a same-origin
        // `data:` URL via the API gateway proxy. This eliminates CORS from the
        // html2canvas path entirely: by the time the canvas paints, no image
        // pixel originates from a foreign origin, so the canvas can never get
        // tainted and `toDataURL()` always succeeds.
        await inlineRemoteImagesAsDataUrls(container);

        // STEP 2 — Block the capture until every image inside the email template
        // has finished downloading (success or failure). The just-rewritten
        // `data:` URLs decode synchronously, but any remaining HTTP-based
        // images still need to settle before we screenshot.
        await waitForAllImagesToLoad(container);

        // Short layout settle so any reflow caused by the now-loaded image
        // dimensions is reflected in scrollWidth / scrollHeight below.
        await new Promise((resolve) => setTimeout(resolve, 120));

        const captureWidth = Math.max(container.scrollWidth, INITIAL_WIDTH);
        if (captureWidth > INITIAL_WIDTH) {
            container.style.width = `${captureWidth}px`;
            await new Promise(resolve => setTimeout(resolve, 100));
        }
        const captureHeight = container.scrollHeight;

        const canvas = await html2canvas(container, {
            width: captureWidth,
            height: captureHeight,
            windowWidth: captureWidth,
            windowHeight: captureHeight,
            backgroundColor: '#ffffff',
            scale: 2,
            logging: false,
            useCORS: true,
            allowTaint: true,
        });

        const scaleRatio = THUMBNAIL_WIDTH / canvas.width;
        const thumbHeight = Math.round(canvas.height * scaleRatio);

        const thumbCanvas = document.createElement('canvas');
        thumbCanvas.width = THUMBNAIL_WIDTH;
        thumbCanvas.height = thumbHeight;
        const ctx = thumbCanvas.getContext('2d');
        if (!ctx) return null;

        ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, THUMBNAIL_WIDTH, thumbHeight);

        return thumbCanvas.toDataURL('image/png');
    } catch (err) {
        console.error('Thumbnail generation failed:', err);
        return null;
    } finally {
        document.body.removeChild(container);
    }
};

const CHANNEL_STYLES: Record<string, { bg: string; accent: string; label: string }> = {
    whatsapp: { bg: '#e7f6e7', accent: '#25d366', label: 'WhatsApp' },
    sms: { bg: '#f3e5f5', accent: '#9c27b0', label: 'SMS' },
    telegram: { bg: '#e3f2fd', accent: '#0088cc', label: 'Telegram' },
    whatsappbusiness: { bg: '#e0f2f1', accent: '#128c7e', label: 'WhatsApp Business' },
};

/**
 * Generates a PNG thumbnail from plain-text template content
 * by rendering it as a styled message card.
 */
export const generateThumbnailFromText = async (
    textContent: string,
    channelType: string,
): Promise<string | null> => {
    if (!textContent || textContent.trim().length === 0) return null;

    const style = CHANNEL_STYLES[channelType] || { bg: '#f5f5f5', accent: '#757575', label: channelType };
    const escaped = textContent
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br/>');

    const html = `
        <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                     background: ${style.bg}; padding: 16px; min-height: 100px;">
            <div style="display: flex; align-items: center; margin-bottom: 12px;">
                <div style="width: 8px; height: 8px; border-radius: 50%; background: ${style.accent}; margin-right: 8px;"></div>
                <span style="font-size: 11px; font-weight: 700; color: ${style.accent}; text-transform: uppercase;
                             letter-spacing: 0.5px;">${style.label} Template</span>
            </div>
            <div style="background: #ffffff; border-radius: 8px; padding: 14px 16px;
                         box-shadow: 0 1px 3px rgba(0,0,0,0.08); border-left: 3px solid ${style.accent};
                         font-size: 13px; line-height: 1.5; color: #333; word-break: break-word;">
                ${escaped}
            </div>
        </div>`;

    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-9999px';
    container.style.top = '0';
    container.style.width = '400px';
    container.style.backgroundColor = '#ffffff';
    container.style.overflow = 'hidden';
    container.innerHTML = html;

    return captureAndCrop(container);
};
